/**
 * Enhanced Today's Schedule Panel
 * 
 * Professional appointment management with status controls,
 * overdue detection, and quick actions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ClockIcon, 
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PhoneIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

import { 
  AppointmentStatus, 
  APPOINTMENT_STATUS_CONFIG,
  AppointmentStatusManager,
  migrateStatus
} from '../../types/appointment-status';
import { StatusBadge, StatusUpdateControls } from './StatusComponents';
import { useAppointmentStatusUpdate, useOverdueAppointmentMonitoring } from '../../services/appointment-status-service';

interface AppointmentWithStatus {
  id: string; // Changed from number to string to match calendar event IDs
  time: string;
  patient: string;
  treatment: string;
  provider: string;
  status: AppointmentStatus;
  duration: string;
  start_time?: string;
  notes?: string;
  phone?: string;
  patientMood?: string;
}

interface TodaysSchedulePanelProps {
  appointments: AppointmentWithStatus[];
  onStatusUpdate?: (appointmentId: string, newStatus: AppointmentStatus) => Promise<void>;
  onNewAppointment?: () => void;
  onRefresh?: () => void;
  onMoodUpdate?: (appointmentId: string, mood: string) => void;
  className?: string;
}

interface StatusCounts {
  [key: string]: number;
}

const TodaysSchedulePanel: React.FC<TodaysSchedulePanelProps> = ({ 
  appointments, 
  onStatusUpdate,
  onNewAppointment,
  onRefresh,
  onMoodUpdate,
  className = '' 
}) => {
  const { t } = useTranslation(['appointments']);
  const [expandedAppointments, setExpandedAppointments] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<AppointmentStatus | 'all'>('all');
  const [isAutoCheckRunning, setIsAutoCheckRunning] = useState(false);
  const [lastAutoCheck, setLastAutoCheck] = useState<Date | null>(null);
  const { updateStatus, loading: isUpdatingStatus } = useAppointmentStatusUpdate();
  const { checkOverdueAppointments, lastCheck: autoDetectionResults } = useOverdueAppointmentMonitoring();

  // Migrate any legacy statuses
  const normalizedAppointments = useMemo(() => {
    return appointments.map(apt => ({
      ...apt,
      status: typeof apt.status === 'string' ? migrateStatus(apt.status) : apt.status
    }));
  }, [appointments]);

  // Filter appointments
  const filteredAppointments = useMemo(() => {
    if (filterStatus === 'all') return normalizedAppointments;
    return normalizedAppointments.filter(apt => apt.status === filterStatus);
  }, [normalizedAppointments, filterStatus]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts: StatusCounts = {};
    normalizedAppointments.forEach(apt => {
      counts[apt.status] = (counts[apt.status] || 0) + 1;
    });
    return counts;
  }, [normalizedAppointments]);

  // Check for overdue appointments
  const overdueAppointments = useMemo(() => {
    return normalizedAppointments.filter(apt => {
      if (apt.status !== 'confirmed') return false;
      
      const appointmentTime = apt.start_time 
        ? new Date(apt.start_time) 
        : new Date(`${new Date().toDateString()} ${apt.time}`);
      
      return AppointmentStatusManager.shouldAutoMarkNoShow(appointmentTime, apt.status);
    });
  }, [normalizedAppointments]);

  // Auto-check for overdue appointments periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      if (overdueAppointments.length > 0) {
        await handleAutoStatusCheck();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [overdueAppointments.length]);

  // Watch for auto detection results and refresh if appointments were updated
  useEffect(() => {
    if (autoDetectionResults && autoDetectionResults.updated > 0 && onRefresh) {
      onRefresh();
    }
  }, [autoDetectionResults, onRefresh]);

  const handleStatusUpdate = async (appointmentId: string, newStatus: AppointmentStatus) => {
    try {
      await updateStatus({
        appointmentId,
        newStatus
      });

      if (onStatusUpdate) {
        await onStatusUpdate(appointmentId, newStatus);
      }

      // Refresh the appointments list
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to update appointment status:', error);
    }
  };

  const handleAutoStatusCheck = async () => {
    setIsAutoCheckRunning(true);
    try {
      await checkOverdueAppointments();
      setLastAutoCheck(new Date());
      
      // The results will be available in autoDetectionResults state
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Auto status check failed:', error);
    } finally {
      setIsAutoCheckRunning(false);
    }
  };

  const toggleExpanded = (appointmentId: string) => {
    const newExpanded = new Set(expandedAppointments);
    if (newExpanded.has(appointmentId)) {
      newExpanded.delete(appointmentId);
    } else {
      newExpanded.add(appointmentId);
    }
    setExpandedAppointments(newExpanded);
  };

  const isOverdue = (appointment: AppointmentWithStatus): boolean => {
    if (appointment.status !== 'confirmed') return false;
    
    const appointmentTime = appointment.start_time 
      ? new Date(appointment.start_time)
      : new Date(`${new Date().toDateString()} ${appointment.time}`);
    
    return AppointmentStatusManager.shouldAutoMarkNoShow(appointmentTime, appointment.status);
  };

  const getAppointmentTime = (appointment: AppointmentWithStatus): Date => {
    return appointment.start_time 
      ? new Date(appointment.start_time)
      : new Date(`${new Date().toDateString()} ${appointment.time}`);
  };

  // Get unique statuses present in appointments
  const availableStatuses = useMemo(() => {
    const statuses = new Set(normalizedAppointments.map(apt => apt.status));
    return Array.from(statuses).sort((a, b) => 
      APPOINTMENT_STATUS_CONFIG[a].priority - APPOINTMENT_STATUS_CONFIG[b].priority
    );
  }, [normalizedAppointments]);

  return (
    <div className={`bg-card rounded-xl border border-border shadow-sm ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-card-foreground">{t('todaysSchedule')}</h2>
          <div className="flex items-center gap-2">
            {overdueAppointments.length > 0 && (
              <button
                onClick={handleAutoStatusCheck}
                disabled={isAutoCheckRunning}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded border border-orange-200 dark:border-orange-700/40 hover:bg-orange-200 dark:hover:bg-orange-900/50 disabled:opacity-50"
              >
                <ExclamationTriangleIcon className="h-3 w-3" />
                {isAutoCheckRunning ? t('checking') : t('overdueCount', { count: overdueAppointments.length })}
              </button>
            )}
            
            <button
              onClick={onRefresh}
              className="p-1 text-muted-foreground hover:text-primary rounded"
              title={t('refreshAppointments')}
            >
              <ClockIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('appointmentsOfTotal', { filtered: filteredAppointments.length, total: normalizedAppointments.length })}
          </p>
          
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AppointmentStatus | 'all')}
            className="text-xs bg-background border border-border rounded px-2 py-1"
          >
            <option value="all">{t('allStatuses')}</option>
            {availableStatuses.map(status => (
              <option key={status} value={status}>
                {APPOINTMENT_STATUS_CONFIG[status].label} ({statusCounts[status] || 0})
              </option>
            ))}
          </select>
        </div>
        
        {/* Quick Stats */}
        <div className="flex gap-4 mt-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span>{t('confirmedCount', { count: statusCounts['confirmed'] || 0 })}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            <span>{t('pendingCount', { count: statusCounts['pending'] || 0 })}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <span>{t('activeCount', { count: (statusCounts['checked-in'] || 0) + (statusCounts['in-progress'] || 0) })}</span>
          </div>
        </div>
      </div>
      
      {/* Appointments List */}
      <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
        {filteredAppointments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ClockIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>{filterStatus !== 'all' ? t('noAppointmentsWithStatus', { status: APPOINTMENT_STATUS_CONFIG[filterStatus as AppointmentStatus]?.label }) : t('noAppointmentsScheduled')}</p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => {
            const appointmentTime = getAppointmentTime(appointment);
            const isAppointmentOverdue = isOverdue(appointment);
            const isExpanded = expandedAppointments.has(appointment.id);
            const canUpdate = !isUpdatingStatus;
            
            return (
              <div
                key={appointment.id}
                className="bg-muted/30 rounded-lg border border-border hover:border-primary/30 transition-all duration-200 hover:shadow-sm group"
              >
                {/* Main Appointment Row */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-icon-primary group-hover:text-primary transition-colors">
                        {appointment.time}
                      </span>
                      {isAppointmentOverdue && (
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-500" title={t('overdue')} />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <StatusBadge status={appointment.status} />
                      
                      <button
                        onClick={() => toggleExpanded(appointment.id)}
                        className="p-1 text-muted-foreground hover:text-primary rounded"
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="font-medium text-card-foreground group-hover:text-primary transition-colors mb-1 flex items-center gap-1.5">
                    {appointment.patient}
                    {appointment.patientMood && appointment.patientMood !== 'neutral' && (
                      <span 
                        className="text-sm cursor-pointer hover:scale-125 transition-transform" 
                        title={`Mood: ${appointment.patientMood}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onMoodUpdate) {
                            // Cycle through moods or open picker - for now just reset to neutral
                            onMoodUpdate(appointment.id, 'neutral');
                          }
                        }}
                      >
                        {({'neutral':'😐','happy':'😊','anxious':'😰','angry':'😠','sad':'😢','pain':'😣','crying':'😭','bored':'😑','suffering':'🤕'} as Record<string,string>)[appointment.patientMood] || '😐'}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground">{appointment.treatment}</p>
                  
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>{appointment.provider}</span>
                    <span>{appointment.duration}</span>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border/50 bg-muted/20">
                    <div className="pt-4 space-y-3">
                      {/* Status Controls */}
                      {canUpdate && (
                        <StatusUpdateControls
                          currentStatus={appointment.status}
                          appointmentId={appointment.id}
                          appointmentTime={appointmentTime}
                          onStatusChanged={handleStatusUpdate}
                          onError={(error) => alert(`${t('statusUpdateFailed')}: ${error}`)}
                          layout="horizontal"
                          showCurrentStatus={false}
                        />
                      )}
                      
                      {/* Quick Actions */}
                      <div className="flex items-center gap-2 pt-2">
                        <button className="flex items-center gap-1 px-3 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20">
                          <EyeIcon className="h-3 w-3" />
                          {t('viewDetails')}
                        </button>
                        
                        {appointment.phone && (
                          <button className="flex items-center gap-1 px-3 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50">
                            <PhoneIcon className="h-3 w-3" />
                            {t('callPatient')}
                          </button>
                        )}
                        
                        {appointment.status === 'confirmed' && (
                          <button 
                            onClick={() => handleStatusUpdate(appointment.id, 'checked-in')}
                            className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                          >
                            <CheckCircleIcon className="h-3 w-3" />
                            {t('checkIn')}
                          </button>
                        )}
                      </div>
                      
                      {/* Notes */}
                      {appointment.notes && (
                        <div className="text-xs text-muted-foreground bg-background rounded p-2">
                          <strong>{t('notes')}:</strong> {appointment.notes}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
        
        {/* Add New Appointment Button */}
        <button 
          onClick={onNewAppointment}
          className="w-full py-3 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-200 flex items-center justify-center space-x-2 group"
        >
          <PlusIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />
          <span>{t('addNewAppointment')}</span>
        </button>
      </div>
      
      {/* Footer Info */}
      {lastAutoCheck && (
        <div className="px-6 py-2 border-t border-border bg-muted/20">
          <p className="text-xs text-muted-foreground text-center">
            {t('lastStatusCheck')}: {lastAutoCheck.toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default TodaysSchedulePanel;